<template>
  <div v-if="props.entries.length > 0" class="guide-entries-layer" />
  <div v-if="props.entries.length > 0" class="guide-entries-modal">
    <div class="guide-header">
      <div>{{ props.entries[0]?.title }}</div>
      <div class="cursor-pointer hover:opacity-75" @click="emit('close')">
        <svg-icon icon="ion:close-outline" />
      </div>
    </div>

    <div class="guide-entries">
      <div
        v-for="(entry, idx) in props.entries"
        :key="entry.id"
        class="guide-entry"
      >
        <div class="guide-sort">
          0{{ idx + 1 }}
        </div>
        <div :class="['guide-icon', entry.id]" />
        <div class="guide-title">
          {{ entry.name }}
        </div>
        <div class="guide-btns">
          <div
            v-for="btn in entry.buttons"
            :key="btn.id"
            :class="['guide-btn', btn.id]"
            @click="handleButtonClick(entry.id, btn)"
          >
            {{ btn.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue';
import SvgIcon from '../SvgIcon.vue';
import { checkSideMenuPermission } from '@core/main';
import { IGuideEntry, IGuideEntryButton } from './guideEntries';
import { ElMessage } from 'element-plus';

const emit = defineEmits(['close', 'click']);

const props = defineProps({
  entries: {
    type: Array as PropType<IGuideEntry[]>,
    default: () => [],
  },
});

const handleButtonClick = async (entryId: string, btn: IGuideEntryButton) => {
  let hasPermission = false;

  // 先判断页面权限
  try {
    const status = await checkSideMenuPermission({
      menuName: btn.code ? undefined : btn.id,
      permissionCode: btn.code,
    });

    hasPermission = status === 1;
  } catch (error) {
    console.error(error);
  }

  if (!hasPermission) return ElMessage.error(btn.errorMsg);

  // 再判断数据权限
  if (btn.dataCheck) {
    const dataPermission = await btn.dataCheck();

    if (!dataPermission) return ElMessage.error(btn.dataErrorMsg);
  }

  // 都通过后，才可以跳转
  emit('click', {
    entryId,
    btn,
  });
};
</script>

<style lang="scss" scoped>
.guide-entries-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.guide-header {
  font-weight: 600;
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.guide-entries-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  width: 800px;
  background-size: 100% 100%;
  background-image: url(./icons/bg.png);
  transform: translate(-50%, -50%);
  z-index: 1000;
  background-color: white;
  border-radius: 5px;
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 16px 24px;
  max-width: 90%;
}

.guide-entries {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.guide-entry {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  width: 142px;
  border-radius: 4px;

  .guide-sort {
    user-select: none;
    font-size: 18px;
    font-weight: 500;
    padding-bottom: 12px;
    color: #A2C9FF;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 13px;
      right: -102px;
      width: 80px;
      height: 1px;
      border-bottom: 1px dashed #A2C9FF;
    }

  }

  .guide-title {
    color: #262626;
    font-size: 14px;
    padding: 8px 0 0 0;
  }

  .guide-icon {
    width: 56px;
    height: 56px;
    background-size: 100% 100%;
    background-repeat: no-repeat;

    &.product {
      background-image: url(./icons/product-icon.png);
    }

    &.task {
      background-image: url(./icons/task-icon.png);
    }

    &.dispatch {
      background-image: url(./icons/dispatch-icon.png);
    }

    &.pay {
      background-image: url(./icons/pay-icon.png);
    }

    &.invoice {
      background-image: url(./icons/invoice-icon.png);
    }
  }

  &:last-child {
    .guide-sort {
      &::after {
        display: none;
      }
    }
  }
}

.guide-btns {
  display: flex;
  gap: 10px;
}

.guide-btn {
  font-size: 12px;
  padding: 6px 0;
  border-radius: 4px;
  color: var(--color-primary);
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    text-decoration: underline;
  }

  &:active {
    opacity: .8;
  }
}
</style>
